import {
  LayoutDashboard,
  Package,
  Scan,
  ClipboardList,
  AlertTriangle,
  CheckCircle,
  FileText,
  Receipt,
  Calculator,
  CalendarRange,
  Landmark,
  History,
  CreditCard,
  Plug,
  RotateCcw,
  AlertOctagon,
  ArrowRightLeft,
  Wrench,
  FileSpreadsheet,
  Users,
  MapPin,
  Building2,
  Clock,
  Boxes,
  CalendarClock,
  Database,
  Layers,
  Truck,
  Warehouse,
  Shield,
  Smartphone,
  Factory,
  GitBranch,
  Settings,
  // Lucide icons referenced by tenant-authored workflow modules. Keep this
  // import list as the contract surface: the JSON's `uiIcon` field must
  // name one of the keys in DYNAMIC_MODULE_ICONS below. Unknown icons
  // fall back to GitBranch so a typo never crashes the nav.
  FlaskConical,
  ShoppingCart,
  ListChecks,
  Beaker,
  type LucideIcon,
} from 'lucide-react';
import { FeatureFlags } from '@/types/feature-flags.types';
import type { DynamicModule, DynamicModuleNavGroup } from '@/api/hooks/useWorkflows';

export interface NavItem {
  titleKey: string; // i18n key for navigation:items.*
  href: string;
  icon?: LucideIcon;
  feature?: keyof FeatureFlags;
  module?: string; // maps to permission module key for visibility filtering
  /**
   * Tenant-administration pages that must be hidden from non-admin users
   * even when they hold the operational permission the item would otherwise
   * gate on (e.g. the workflow designer, which shares the `workflows`
   * permission with the operator scan tiles). Combined with `module` when
   * both are present: the item shows only when the permission AND isAdmin
   * both pass. Non-admin roles (e.g. client operators) never see these.
   */
  adminOnly?: boolean;
  children?: NavItem[];
  /**
   * P08 — pending-work count for this nav item. Currently only set on
   * dynamic-flow modules (asset_state_queue / client_hub / shipment_queue).
   * Null / undefined → no badge. Zero → no badge (suppress noise).
   */
  badge?: number | null;
}

export interface NavGroup {
  titleKey: string; // i18n key for navigation:groups.*
  items: NavItem[];
}

/**
 * Whitelisted Lucide icons that a tenant-authored workflow JSON may
 * reference from a module's `uiIcon`. Anything outside this map falls
 * back to the GitBranch glyph so a misspelled icon never breaks the
 * left nav. Add new entries here as new verticals demand them.
 */
const DYNAMIC_MODULE_ICONS: Record<string, LucideIcon> = {
  Building2,
  FlaskConical,
  Beaker,
  ShoppingCart,
  ListChecks,
  Truck,
  Warehouse,
  Package,
  GitBranch,
  FileText,
  FileSpreadsheet,
  RotateCcw,
  AlertOctagon,
  Wrench,
  ArrowRightLeft,
  Boxes,
};

export function resolveDynamicIcon(name: string | null | undefined): LucideIcon {
  if (!name) return GitBranch;
  return DYNAMIC_MODULE_ICONS[name] ?? GitBranch;
}

export function getNavigation(
  features: FeatureFlags,
  dynamicModules: DynamicModule[] = []
): NavGroup[] {
  const nav: NavGroup[] = [
    {
      titleKey: 'groups.main',
      items: [
        {
          titleKey: 'items.dashboard',
          href: '/dashboard',
          icon: LayoutDashboard,
        },
        // Ask ActiveID is not a sidebar module — it's a global right-side drawer
        // (AskAssistantDrawer), opened from the header button or Ctrl/Cmd+K.
      ],
    },
    {
      titleKey: 'groups.operations',
      items: [
        {
          titleKey: 'items.assets',
          href: '/assets',
          icon: Package,
        },
        // Mapping-driven asset import (reusable profiles + wizard). Gated by
        // the `activos` permission module so it tracks asset visibility.
        {
          titleKey: 'items.assetImport',
          href: '/assets/import/profiles',
          icon: FileSpreadsheet,
          module: 'activos',
        },
        // Gated on the 'epcs' permission module, which the API now enforces
        // too (EpcController was previously [Authorize]-only). Roles are
        // grandfathered into epcs.view by
        // migrate_epc_permissions_*, so this hides nothing on deploy — it
        // just makes revoking the permission actually take effect in the nav.
        {
          titleKey: 'items.epcs',
          href: '/epcs',
          icon: Scan,
          module: 'epcs',
        },
        // Pallet manufacture is TOCA's OWN operation, not a client-facing one:
        // TOCA sends EPCs to an external provider and gets finished pallets
        // back, and no client is involved anywhere in that exchange. It sits
        // next to the EPC pool because that pool is what a manufacture order
        // allocates from. Gated per item like palletIncidents below, so the
        // group needs no conditional of its own.
        {
          titleKey: 'items.manufactureProviders',
          href: '/pallet-manufacture/providers',
          icon: Factory,
          feature: 'palletManufacture',
          module: 'palletManufacture',
        },
        {
          titleKey: 'items.manufactureOrders',
          href: '/pallet-manufacture/orders',
          icon: Boxes,
          feature: 'palletManufacture',
          module: 'palletManufacture',
        },
        {
          titleKey: 'items.inventory',
          href: '/inventory',
          icon: ClipboardList,
        },
        // Two modules share the "Incidentes" label and slot, gated by mutually
        // exclusive flags: SGS/Demo get the inventory-process incidents module,
        // TOCA gets pallet incidents (client losses / quality returns). A
        // tenant should never have both flags on — System Config exposes them
        // separately, so that pairing is a configuration error, not a bug here.
        {
          titleKey: 'items.incidents',
          href: '/incidents',
          icon: AlertTriangle,
          feature: 'incidents',
        },
        // Change Governance — the Approval Center. Doubly gated: the
        // `approvalsV2` flag is off by default for every tenant, and the
        // `approvals` module is in STRICT_MODULES so it is deny-by-default
        // rather than allow-by-default like most nav entries.
        {
          titleKey: 'items.incidents',
          href: '/pallet-incidents',
          icon: AlertTriangle,
          feature: 'palletIncidents',
          module: 'incidentes_tarimas',
        },
        {
          titleKey: 'items.approvals',
          href: '/approvals',
          icon: CheckCircle,
          feature: 'approvalsV2',
          module: 'approvals',
        },
        // Phase 2.5 — Dynamic-workflow shipments operator queues. Gated
        // by the `workflows` flag (off where the engine is: SGS) and the
        // "shipments" permission module. The flag is what hides it on SGS:
        // platform admins see every non-strict module regardless of role
        // permissions. TOCA tenants that have never bound a shipment-aware
        // workflow see the page render with three empty "Nothing pending."
        // sections — no confusion vs. the legacy Requisitions / Recovery /
        // Damage pages, which keep working as before.
        {
          titleKey: 'items.shipments',
          href: '/shipments',
          icon: Truck,
          feature: 'workflows',
          module: 'shipments',
        },
        // A2 — WorkflowOrder (RFP/PO) pages. Gated by the `workflows` flag
        // and the "orders" permission module seeded in
        // migrate_workflow_order_permissions_*; tenants that never grant it
        // simply don't see the entry.
        {
          titleKey: 'items.orders',
          href: '/orders',
          icon: ClipboardList,
          feature: 'workflows',
          module: 'orders',
        },
      ],
    },
  ];

  // Add Client Operations group (combines clients hub + pallet operations)
  if (features.clients) {
    const clientOperationsItems: NavItem[] = [
      // Client Hub as first item
      {
        titleKey: 'items.clientsHub',
        href: '/clients-hub',
        icon: Building2,
        feature: 'clients',
      },
    ];

    // Add pallet operations under the same group
    if (features.requisitions) {
      clientOperationsItems.push({
        titleKey: 'items.requisitions',
        href: '/requisitions',
        icon: FileText,
        feature: 'requisitions',
      });
    }

    // Legacy pallet transfers ride the requisitions vertical: a tenant with
    // client operations but no requisition flow (PalletCenter) has no
    // transfers page to offer. TOCA carries both flags; SGS/DEMO neither.
    if (features.clients && features.requisitions) {
      clientOperationsItems.push({
        titleKey: 'items.transfers',
        href: '/transfers',
        icon: ArrowRightLeft,
        feature: 'clients',
      });
    }

    if (features.recovery) {
      clientOperationsItems.push({
        titleKey: 'items.recovery',
        href: '/recovery',
        icon: RotateCcw,
        feature: 'recovery',
      });
    }

    if (features.damage) {
      clientOperationsItems.push({
        titleKey: 'items.damage',
        href: '/damage',
        icon: AlertOctagon,
        feature: 'damage',
      });
    }

    if (features.repair) {
      clientOperationsItems.push({
        titleKey: 'items.repairs',
        href: '/repairs',
        icon: Wrench,
        feature: 'repair',
      });
    }

    nav.push({
      titleKey: 'groups.clientManagement',
      items: clientOperationsItems,
    });
  }

  if (features.reports) {
    nav.push({
      titleKey: 'groups.reporting',
      items: [
        {
          titleKey: 'items.reports',
          href: '/reports',
          icon: FileSpreadsheet,
          feature: 'reports',
        },
      ],
    });
  }

  // Add Maintenance group
  if (features.maintenance) {
    nav.push({
      titleKey: 'groups.maintenance',
      items: [
        {
          titleKey: 'items.maintenanceDashboard',
          href: '/maintenance',
          icon: LayoutDashboard,
          feature: 'maintenance',
        },
        {
          titleKey: 'items.workOrders',
          href: '/maintenance/work-orders',
          icon: Wrench,
          feature: 'maintenance',
        },
        {
          titleKey: 'items.scheduling',
          href: '/maintenance/scheduling',
          icon: CalendarClock,
          feature: 'maintenance',
        },
        {
          titleKey: 'items.maintenancePlans',
          href: '/maintenance/plans',
          icon: Clock,
          feature: 'maintenance',
        },
        {
          titleKey: 'items.partsInventory',
          href: '/maintenance/parts',
          icon: Boxes,
          feature: 'maintenance',
        },
        {
          titleKey: 'items.maintenanceAlerts',
          href: '/maintenance/alerts',
          icon: AlertTriangle,
          feature: 'maintenance',
        },
        // "Catalogs" used to live here; it is now global — see the Administration
        // group below. Asset classes, problem codes, etc. are cross-cutting and
        // do not belong exclusively to maintenance.
      ],
    });
  }

  // Production Operations
  if (features.productionOperations) {
    nav.push({
      titleKey: 'groups.productionOperations',
      items: [
        {
          titleKey: 'items.fleetOverview',
          href: '/fleet',
          icon: Truck,
          feature: 'productionOperations',
        },
        {
          titleKey: 'items.productionOrders',
          href: '/production-orders',
          icon: ClipboardList,
          feature: 'productionOperations',
        },
        {
          titleKey: 'items.warehouseOps',
          href: '/warehouse',
          icon: Warehouse,
          feature: 'productionOperations',
        },
      ],
    });
  }

  // Billing & e-Invoicing — TOCA pallet-stay billing + CFDI 4.0.
  // Gated by the `billing` feature flag; SGS/DEMO tenants never see it.
  if (features.billing) {
    nav.push({
      titleKey: 'groups.billing',
      items: [
        {
          titleKey: 'items.billingDashboard',
          href: '/billing',
          icon: LayoutDashboard,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.cfdiInvoices',
          href: '/billing/invoices',
          icon: Receipt,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.newInvoice',
          href: '/billing/invoices/new',
          icon: FileText,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.payments',
          href: '/billing/payments/new',
          icon: CreditCard,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.tariffPlans',
          href: '/billing/tariffs',
          icon: Calculator,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.tariffAssignments',
          href: '/billing/assignments',
          icon: Calculator,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.inventoryStays',
          href: '/billing/stays',
          icon: Package,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.billingPeriods',
          href: '/billing/periods',
          icon: CalendarRange,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.fiscalSetup',
          href: '/billing/fiscal',
          icon: Landmark,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.pacConfig',
          href: '/billing/pac',
          icon: Plug,
          feature: 'billing',
          module: 'billing',
        },
        {
          titleKey: 'items.billingAudit',
          href: '/billing/audit',
          icon: History,
          feature: 'billing',
          module: 'billing',
        },
      ],
    });
  }

  // Administration - all catalog/admin items live here now. The standalone
  // "Catalogs" group was folded into Administración because everything in it
  // (Users, Locations, Roles, cross-cutting catalogs) is admin work.
  const adminItems: NavItem[] = [
    {
      titleKey: 'items.users',
      href: '/catalogs/users',
      icon: Users,
      module: 'users',
    },
    {
      titleKey: 'items.locations',
      href: '/catalogs/locations',
      icon: MapPin,
      module: 'locations',
    },
    {
      titleKey: 'items.roles',
      href: '/catalogs/roles',
      icon: Shield,
      module: 'roles',
    },
    {
      titleKey: 'items.deviceManagement',
      href: '/admin/devices',
      icon: Smartphone,
      module: 'deviceManagement',
    },
  ];

  if (features.clients) {
    adminItems.push({
      titleKey: 'items.clients',
      href: '/catalogs/clients',
      icon: Building2,
      feature: 'clients',
    });
  }

  // Catalog hub (cross-cutting lookups incl. asset classes via the merged
  // editor). Gated by maintenance feature because most of its sub-catalogs
  // ship with that module; remove the feature check if your tenant surfaces
  // non-maintenance catalogs here.
  if (features.maintenance) {
    adminItems.push({
      titleKey: 'items.catalogsHub',
      href: '/catalogs',
      icon: Database,
      feature: 'maintenance',
    });
  }

  adminItems.push({
    titleKey: 'items.metadataSchemas',
    href: '/admin/metadata/schemas',
    icon: Layers,
    adminOnly: true,
  });

  // Phase 2: Dynamic Workflows admin (the flow DESIGNER). Gated by the
  // "workflows" permission AND adminOnly: operators need workflows:view for
  // their scan tiles, but authoring flows is a tenant-admin task — so the
  // designer stays hidden for non-admin roles (e.g. client operators) while
  // their tiles remain. The page 404s when Workflows:Enabled is false (SGS);
  // the `workflows` feature flag is what keeps it out of the SGS menu, since
  // platform admins bypass the permission check.
  adminItems.push({
    titleKey: 'items.workflows',
    href: '/admin/workflows',
    icon: GitBranch,
    feature: 'workflows',
    module: 'workflows',
    adminOnly: true,
  });

  adminItems.push({
    titleKey: 'items.systemConfig',
    href: '/admin/system-config',
    icon: Settings,
    adminOnly: true,
  });

  nav.push({
    titleKey: 'groups.admin',
    items: adminItems,
  });

  // Phase 11 — splice tenant-authored modules in by navGroup. Each
  // dynamic module lands as a NavItem with module="workflows" so the
  // existing permission filter keeps it hidden for users that lack
  // the workflows grant. The catch-all route /dynamic/:code handles
  // the click; per-type page templates land in Phase 12.
  if (dynamicModules.length > 0) {
    const byGroup = new Map<DynamicModuleNavGroup, NavItem[]>();
    const sorted = [...dynamicModules].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
    );
    for (const m of sorted) {
      const item: NavItem = {
        titleKey: m.name, // tenant data, not an i18n key — t() will fall through
        href: `/dynamic/${m.code}`,
        icon: resolveDynamicIcon(m.uiIcon),
        module: m.permissionModule ?? 'workflows',
        // P08 — pass through the badge count. The renderer is responsible
        // for the "zero → no badge" rule.
        badge: m.count,
      };
      const bucket = byGroup.get(m.navGroup) ?? [];
      bucket.push(item);
      byGroup.set(m.navGroup, bucket);
    }

    const groupTitleByKey: Record<DynamicModuleNavGroup, string> = {
      operations: 'groups.operations',
      clientManagement: 'groups.clientManagement',
      reports: 'groups.reporting',
    };

    for (const [groupKey, items] of byGroup.entries()) {
      const titleKey = groupTitleByKey[groupKey];
      const existing = nav.find((g) => g.titleKey === titleKey);
      if (existing) {
        existing.items.push(...items);
      } else {
        // Group doesn't exist yet for this tenant (e.g. a Hospital
        // tenant whose feature flags hide every legacy clientManagement
        // item) — surface a fresh group so the dynamic items still
        // have a visible home.
        nav.push({ titleKey, items });
      }
    }
  }

  return nav;
}
